import {Alert, Box, CircularProgress, Stack, Typography} from "@mui/material";
import {useFlag} from "@variocube/app-ui";
import {Html5Qrcode} from "html5-qrcode";
import React, {useEffect, useRef} from "react";
import {useLocalization} from "../i18n";

interface QrcodeScannerProps {
	onScanned: (value: string) => void;
	/** While true, decoded values are ignored (the camera keeps running). */
	paused?: boolean;
}

const readerId = "qrcode-reader";

/**
 * Inline QR-code scanner. The camera runs for the lifetime of the component and renders into a fixed-aspect
 * box, so the surrounding layout already has its final size while the camera stream is still starting up.
 */
export function QrcodeScanner({onScanned, paused = false}: QrcodeScannerProps) {
	const {t} = useLocalization();

	const [ready, setReady] = useFlag(false);
	const [startError, setStartError] = useFlag(false);

	// The decode callback fires outside React at camera fps; route it through a ref so the one-shot
	// camera effect always sees the current handler and paused state.
	const scanRef = useRef({onScanned, paused});
	scanRef.current = {onScanned, paused};

	useEffect(() => {
		const scanner = new Html5Qrcode(readerId);
		const started = scanner
			.start(
				{facingMode: "environment"},
				{fps: 10, aspectRatio: 1},
				value => {
					if (!scanRef.current.paused) {
						scanRef.current.onScanned(value);
					}
				},
				() => {
					// Per-frame decode misses are expected while the customer aims the camera.
				},
			)
			.then(() => {
				console.info("QR-Code camera started.");
				setReady();
				return true;
			})
			.catch(err => {
				console.error("Failed to start QR-Code camera", err);
				setStartError();
				return false;
			});
		return () => {
			started.then(running => {
				if (running) {
					scanner.stop()
						.then(() => console.info("QR-Code camera stopped."))
						.catch(err => console.error("Failed to stop QR-Code camera", err));
				}
			});
		};
	}, []);

	return (
		<Box
			sx={{
				position: "relative",
				width: "100%",
				maxWidth: 300,
				aspectRatio: "1 / 1",
				overflow: "hidden",
				borderRadius: 1,
				bgcolor: "common.black",
			}}
		>
			<Box
				id={readerId}
				sx={{
					position: "absolute",
					inset: 0,
					"& video": {width: "100%", height: "100%", objectFit: "cover"},
				}}
			/>
			{!ready && (
				<Stack
					sx={{position: "absolute", inset: 0, p: 2}}
					alignItems="center"
					justifyContent="center"
					gap={2}
				>
					{startError
						? <Alert severity="error">{t("location.scanStartError")}</Alert>
						: (
							<>
								<CircularProgress sx={{color: "common.white"}} />
								<Typography color="common.white">{t("location.scanStartHint")}</Typography>
							</>
						)}
				</Stack>
			)}
		</Box>
	);
}
